import { locale } from '../lib/locale';

/**
 * "Heute", the resting wall and what appears on them (1.19): greeting, day plan, family goal,
 * weather, festive days, the cards for push and the home screen. German is the master.
 */
export const TODAY_TEXT = {
  de: {
    morning: 'Guten Morgen',
    hello: 'Hallo',
    evening: 'Guten Abend',
    night: 'Gute Nacht',
    greeting: (hello: string, who: string) => `${hello}, ${who}!`,
    family: 'Familie',
    familyMode: {
      title: 'Habt ihr ein Gerät, das alle nutzen?',
      tabletTitle: 'Nutzt ihr dieses Gerät alle gemeinsam?',
      text: 'In der Küche, im Flur oder an der Wand: Im Familienmodus bleibt es an, wird nachts ruhig, erinnert mit Gong, und Kinder können nichts löschen. Den Elternbereich öffnet ihr mit der PIN.',
      switchThis: 'Dieses Gerät in den Familienmodus schalten',
      pairOther: 'Anderes Gerät koppeln',
      pairDevice: 'Gerät koppeln',
      signOutNote:
        'Du wirst auf diesem Gerät abgemeldet – auf deinem Handy bleibst du angemeldet. Hier öffnest du den Elternbereich danach mit der Eltern-PIN.',
      name: 'Name des Geräts',
      namePlaceholder: 'z. B. Küche, Flur, Wohnzimmer',
      switchNow: 'Jetzt umschalten',
      cancel: 'Abbrechen',
      later: 'Hinweis zum Familienmodus schließen',
    },
    noHome: 'Wohnort fehlt – dann zeigen wir euer Wetter und euren Himmel.',
    addHome: 'Wohnort eintragen',
    dayPlan: 'Tagesplan',
    calendar: 'Kalender',
    nothingToday: 'Heute steht nichts im Kalender.',
    nothingTodayHint:
      'Termine trägst du über „＋“ ein – oder verbinde Schule, Verein und Müllabfuhr in den Einstellungen.',
    tasksToday: 'Aufgaben heute',
    allTasks: 'Alle Aufgaben',
    nextDays: 'Die nächsten Tage',
    together: 'Gemeinsam',
    goalLabel: (title: string) => `Familienziel ${title}`,
    goalValue: (progress: string, target: string) => `${progress} von ${target} Sternen`,
    goalTitle: (title: string) => `Familienziel: ${title}`,
    goalReached: 'Geschafft! Zeit fürs Familienziel 🎉',
    goalLeft: (left: string) => `Jeder Stern von allen zählt mit – noch ${left}.`,
    kudos: 'Familienmoment',
    kudosHint: 'Ein Familienmoment würdigt gutes Miteinander – über „＋“.',
    weatherIn: (place: string) => `Wetter in ${place}`,
    yourPlace: 'eurem Ort',
    routineDone: (name: string, routine: string) => `${name}: ${routine} geschafft!`,
    wellDone: 'Super gemacht',
    routines: { morning: 'Morgenroutine', afternoon: 'Nachmittag', evening: 'Abendroutine' },
    // resting wall
    nightRest: 'Nachtruhe',
    rest: 'Ruhebild',
    upNextAt: (when: string) => `Als Nächstes · ${when}`,
    pinboard: 'Pinnwand',
    backToOverview: 'Zurück zur Übersicht',
    goodToKnow: 'Gut zu wissen',
    // festive days
    birthday: 'Geburtstag',
    festiveDay: 'Festtag',
    birthdayBand: (names: string) => `Heute hat ${names} Geburtstag – alles Gute!`,
    birthdayTitle: (names: string) => `Alles Gute, ${names}!`,
    birthdayHeadline: (names: string) => `Happy Birthday, ${names}!`,
    celebrate: 'Heute wird gefeiert!',
    niceDays: 'Schöne Tage euch allen.',
    continue: 'Weiter',
    // cards on top
    pushLabel: 'Erinnerungen aufs Handy',
    pushTitle: 'Erinnerungen aufs Handy?',
    pushIphone:
      'Auf dem iPhone geht das, wenn Larovi Home als App installiert ist: im Safari auf „Teilen“ tippen, dann „Zum Home-Bildschirm“ – und die App von dort öffnen.',
    pushText:
      '30 Minuten vor Terminen, Freigaben und Geburtstage – auch wenn die App zu ist. Lässt sich jederzeit unter Einstellungen → Dieses Gerät ändern.',
    switchOn: 'Einschalten',
    understood: 'Verstanden',
    later: 'Später',
    installLabel: 'Auf den Startbildschirm',
    installTitle: 'Larovi Home auf den Startbildschirm?',
    installText:
      'Dann startet es wie eine App, ohne Browserleiste – mit einem Tipp vom Startbildschirm.',
    install: 'Installieren',
    dontShow: 'Nicht mehr zeigen',
    whatsNew: 'Was ist neu',
    look: 'Ansehen',
    // weather
    weather: {
      storm: 'Gewitter',
      snow: 'Schnee',
      rain: 'Regen',
      fog: 'Nebel',
      cloudy: 'Bewölkt',
      fair: 'Heiter',
      sunny: 'Sonnig',
      rainFrom: (hour: string) => `Regen ab ${hour}`,
      dry: (summary: string) => `${summary}, trocken`,
      umbrella: 'Schirm einpacken',
      hat: 'Mütze & Handschuhe',
      jacket: 'Jacke nicht vergessen',
    },
  },
  en: {
    morning: 'Good morning',
    hello: 'Hello',
    evening: 'Good evening',
    night: 'Good night',
    greeting: (hello: string, who: string) => `${hello}, ${who}!`,
    family: 'family',
    familyMode: {
      title: 'Do you have a device everyone uses?',
      tabletTitle: 'Do you all use this device together?',
      text: 'In the kitchen, the hall or on the wall: in family mode it stays on, goes quiet at night, reminds you with a chime, and kids can’t delete anything. Parents open their area with the PIN.',
      switchThis: 'Switch this device to family mode',
      pairOther: 'Pair another device',
      pairDevice: 'Pair a device',
      signOutNote:
        'You’ll be signed out on this device – you stay signed in on your phone. Here, you then open the parent area with the parent PIN.',
      name: 'Device name',
      namePlaceholder: 'e.g. kitchen, hall, living room',
      switchNow: 'Switch now',
      cancel: 'Cancel',
      later: 'Close the family mode tip',
    },
    noHome: 'Your town is missing – add it and we’ll show your weather and your sky.',
    addHome: 'Add your town',
    dayPlan: 'Day plan',
    calendar: 'Calendar',
    nothingToday: 'Nothing in the calendar today.',
    nothingTodayHint:
      'Add events with “＋” – or connect school, clubs and bin collection in Settings.',
    tasksToday: 'Tasks today',
    allTasks: 'All tasks',
    nextDays: 'The next few days',
    together: 'Together',
    goalLabel: (title: string) => `Family goal ${title}`,
    goalValue: (progress: string, target: string) => `${progress} of ${target} stars`,
    goalTitle: (title: string) => `Family goal: ${title}`,
    goalReached: 'Done it! Time for the family goal 🎉',
    goalLeft: (left: string) => `Every star from everyone counts – ${left} to go.`,
    kudos: 'Family moment',
    kudosHint: 'A family moment celebrates being good to each other – via “＋”.',
    weatherIn: (place: string) => `Weather in ${place}`,
    yourPlace: 'your town',
    routineDone: (name: string, routine: string) => `${name}: ${routine} done!`,
    wellDone: 'Well done',
    routines: { morning: 'morning routine', afternoon: 'afternoon', evening: 'evening routine' },
    nightRest: 'Night rest',
    rest: 'Rest screen',
    upNextAt: (when: string) => `Up next · ${when}`,
    pinboard: 'Pinboard',
    backToOverview: 'Back to the overview',
    goodToKnow: 'Good to know',
    birthday: 'Birthday',
    festiveDay: 'Festive day',
    birthdayBand: (names: string) => `It’s ${names}’s birthday today – happy birthday!`,
    birthdayTitle: (names: string) => `Happy birthday, ${names}!`,
    birthdayHeadline: (names: string) => `Happy Birthday, ${names}!`,
    celebrate: 'Time to celebrate!',
    niceDays: 'Lovely days to you all.',
    continue: 'Continue',
    pushLabel: 'Reminders on your phone',
    pushTitle: 'Reminders on your phone?',
    pushIphone:
      'On an iPhone this works once Larovi Home is installed as an app: in Safari tap “Share”, then “Add to Home Screen” – and open the app from there.',
    pushText:
      '30 minutes before events, approvals and birthdays – even when the app is closed. You can change it any time under Settings → This device.',
    switchOn: 'Turn on',
    understood: 'Got it',
    later: 'Later',
    installLabel: 'Add to home screen',
    installTitle: 'Add Larovi Home to your home screen?',
    installText: 'Then it starts like an app, without the browser bar – with one tap.',
    install: 'Install',
    dontShow: 'Don’t show again',
    whatsNew: 'What’s new',
    look: 'Take a look',
    weather: {
      storm: 'Thunderstorm',
      snow: 'Snow',
      rain: 'Rain',
      fog: 'Fog',
      cloudy: 'Cloudy',
      fair: 'Fair',
      sunny: 'Sunny',
      rainFrom: (hour: string) => `Rain from ${hour}`,
      dry: (summary: string) => `${summary}, dry`,
      umbrella: 'Take an umbrella',
      hat: 'Hat & gloves',
      jacket: 'Don’t forget a jacket',
    },
  },
} as const;

export const todayText = TODAY_TEXT[locale];
